import { cn, NoddiIcon } from 'noddi-ui-common';
import { NoddiDateFormats, noddiFormatDate } from 'noddi-util';
import { MouseEvent, ReactNode, useId, useState } from 'react';
import { Matcher, PropsBase } from 'react-day-picker';
import { Button } from '../../../base/button/button';
import { Calendar } from '../../../base/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../NoddiPopover';
import { NoddiTooltip } from '../NoddiTooltip';
import { ClearButton } from './ClearButton';

export interface NoddiDatePickerProps {
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  id?: string;
  name?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;

  disabled?: Matcher | Matcher[] | undefined;
  showClearButton?: boolean;
  label?: string;
  customTrigger?: ReactNode;
  popoverContentClassName?: string;
  timeZone?: string;
  tooltip?: string;
  disableHighlightToday?: boolean;
  hidden?: Matcher | Matcher[] | undefined;
  showOutsideDays?: boolean;
  className?: string;
  captionLayout?: PropsBase['captionLayout'];
  translation?: { pickADate: string };
}

export function NoddiDatePicker({
  date,
  onDateChange,
  disabled,
  showClearButton,
  id,
  name,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  label,
  customTrigger,
  popoverContentClassName,
  timeZone,
  tooltip,
  disableHighlightToday = false,
  hidden,
  showOutsideDays = true,
  className,
  captionLayout,
  translation
}: NoddiDatePickerProps) {
  const safeTranslation = translation ?? { pickADate: 'Pick a date' };
  const generatedId = useId();
  const controlId = id ?? name ?? generatedId;
  const labelIdentifier = `${controlId}-label`;
  const valueIdentifier = `${controlId}-value`;
  const [isOpen, setIsOpen] = useState(false);

  const handleClearDate = (e: MouseEvent) => {
    e.stopPropagation();
    onDateChange(undefined);
  };
  return (
    <div className='flex flex-col gap-0.5'>
      {label && (
        <div className='flex items-center gap-2'>
          <label id={labelIdentifier} htmlFor={controlId} className='text-sm'>
            {label}
          </label>
          {tooltip && <NoddiTooltip content={tooltip} label={`More information about ${label}`} />}
        </div>
      )}
      {/* Anchor box for the clear button: mirrors the trigger's sizing classes so the
          absolutely positioned clear button tracks the trigger, not the stretched column. */}
      <div className={cn('relative', className)}>
        <Popover open={isOpen} onOpenChange={setIsOpen} modal>
          <PopoverTrigger asChild>
            {customTrigger ? (
              customTrigger
            ) : (
              <Button
                type='button'
                variant='outline'
                id={controlId}
                name={name}
                aria-labelledby={label ? `${labelIdentifier} ${valueIdentifier}` : valueIdentifier}
                aria-describedby={ariaDescribedBy}
                aria-invalid={ariaInvalid}
                aria-haspopup='dialog'
                aria-expanded={isOpen}
                className={cn(
                  'relative h-9 w-full min-w-70 justify-start text-left text-base font-normal md:text-sm',
                  !date && 'text-muted-foreground',
                  date && showClearButton && 'pr-12',
                  className
                )}
              >
                <NoddiIcon name='Calendar' />
                <span id={valueIdentifier}>
                  {date ? noddiFormatDate(date, NoddiDateFormats.DATE) : safeTranslation.pickADate}
                </span>
              </Button>
            )}
          </PopoverTrigger>
          <PopoverContent
            className={cn(
              'max-h-(--radix-popover-content-available-height) w-auto overflow-x-hidden overflow-y-auto p-0',
              popoverContentClassName
            )}
          >
            <Calendar
              // if number of months is 1 ( default value), its nice to hardcode the width, so it doesn't change when hiding days.
              className='w-62'
              disableHighlightToday={disableHighlightToday}
              disabled={disabled}
              mode='single'
              timeZone={timeZone}
              selected={date}
              captionLayout={captionLayout}
              onSelect={(selectedDate) => {
                onDateChange(selectedDate);
                setIsOpen(false);
              }}
              // oxlint-disable-next-line jsx-a11y/no-autofocus -- calendar inside a popover the user just opened; focus has to move off the trigger for arrow-key date navigation
              autoFocus
              hidden={hidden}
              showOutsideDays={showOutsideDays}
              weekStartsOn={1}
            />
          </PopoverContent>
        </Popover>
        {date && showClearButton && (
          <ClearButton handleClearDate={handleClearDate} label={label ? `Clear ${label}` : undefined} />
        )}
      </div>
    </div>
  );
}
